import React, { useEffect, useMemo } from "react";

/**
 * Single-file, preview-safe article page.
 * - No external imports (prevents "File not found" errors in the editor).
 * - Paste your legacy HTML into `legacyHtml` and it will render inside the site frame.
 * - Strips <head>/<style>/<script>/<link> so it doesn't break your app styling.
 * - Optional image rewrite: /images/... -> /images/articles/...
 */

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z" fill="#0b0b0f" />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                Culture × Music
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Art × Fashion
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent 
            <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical (safe in most apps; in this preview it’s fine too)
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">Culture × Music</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Feature</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Read</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">Africa Loud</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Director's pick
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

export default function BeyonceArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="YOUR HISTORY IS IN YOUR LANGUAGE"
      seoDescription="16/08/2025."
      canonicalPath="/culture-music/town_talk"
      heroTitle="YOUR HISTORY IS IN YOUR LANGUAGE"
      heroByline="Written by Olwethu M. Bebeza • Africa Loud Culture Desk"
      
      heroImage="/images/articles/#"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`

      
<div id="main-contents">

            <h1></h1>
  <blockquote>
    Have you ever asked yourself, <strong>“Is my history in my language?"</strong> 
    The answer is a resounding <strong>yes</strong>.
  </blockquote>

  <h2>Language as Identity</h2>
  <p>
    Everyone has a home language, a mother tongue that connects them to their roots. 
    That’s why, when people struggle to express themselves in another language, they often say, 
    <em>“Excuse me, it’s not my mother tongue.”</em> 
    Language is not just a tool for communication; it is a living archive of who we are, 
    where we come from, and what we value.
  </p>

  <p>
    In Africa, our diverse cultures are matched by a rich tapestry of languages that make each community unique. 
    Language is deeply intertwined with identity, and yet, in a world that sometimes rewards assimilation over authenticity, 
    many young people shy away from speaking their home languages.
  </p>

  <blockquote>
    Some do this to avoid being judged, choosing instead to communicate in more "acceptable" or dominant languages. 
    But in doing so, they often mask their true identities.
  </blockquote>

  <p>
    For example, imagine starting a new job. You're from Botswana and speak Setswana, 
    but you greet your colleagues in isiXhosa because it’s a language you’ve grown to love. 
    Without asking any questions, people may assume you're Xhosa and from the Eastern Cape—proving how we often judge people 
    based on how and what they speak, before getting to know them.
  </p>

  <h2>Roots and Connection</h2>
  <p>
    Loving and embracing your roots starts with being proud of your language. 
    It can also be a bridge to deeper human connection. 
    When you meet someone who speaks the same language as you—perhaps at an event where you knew no one— 
    you immediately feel a sense of comfort and belonging. 
  </p>

  <blockquote>
    In isiZulu, we call this <strong>“umkhaya”</strong>, a fellow community member.  
    That shared language can spark a conversation, foster trust, and even build lasting relationships.
  </blockquote>

  <h2>Language as Legacy</h2>
  <p>
    Language is a powerful carrier of culture and heritage. 
    When you speak your home language, you are not just using words, you are conveying a legacy. 
    People can often tell where you're from, your cultural practices, and your historical background, 
    just by listening to you speak.
  </p>

  <p>
    But knowing a language is not enough. Understanding the history behind it is equally important. 
    Too many of us speak our mother tongues without truly understanding the stories, struggles, and triumphs they carry.
  </p>

  <h2>Answering the Question</h2>
  <blockquote>
    So, is your history in your language? <br><br>
    <strong>Absolutely.</strong>
  </blockquote>

  <p>
    The moment someone hears you speak isiZulu; they might immediately think of Shaka Zulu. 
    When you speak isiXhosa, Nelson Mandela often comes to mind. 
    These are not just names, they are symbols of history, culture, and pride rooted in language.
  </p>

  <h2>Closing Thoughts</h2>
  <p>
    Our stories live in our speech. <br>
    Our history is in our language. <br>
    <em>Let’s not forget it, let’s celebrate it.</em>
</p>
  

 `}
    />
  );
}
